import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Plus, Edit, Trash2 } from 'lucide-react';

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      const data = await api.getSources();
      setSources(data.sources);
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSource(null);
    setShowModal(true);
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setShowModal(true);
  };

  const handleSave = async (sourceData) => {
    try {
      if (editingSource) {
        await api.updateSource(editingSource.id, sourceData);
      } else {
        await api.createSource(sourceData);
      }
      await loadSources();
      setShowModal(false);
    } catch (error) {
      alert('Chyba při ukládání: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Opravdu chcete smazat tento zdroj?')) return;

    try {
      await api.deleteSource(id);
      setSources(sources.filter(s => s.id !== id));
    } catch (error) {
      alert('Chyba při mazání zdroje: ' + error.message);
    }
  };

  if (loading) {
    return <div>Načítání...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Zdroje emailů</h1>
        <button onClick={handleAdd} className="btn btn-primary flex items-center space-x-2">
          <Plus size={20} />
          <span>Přidat zdroj</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sources.map(source => (
          <SourceCard
            key={source.id}
            source={source}
            onEdit={() => handleEdit(source)}
            onDelete={() => handleDelete(source.id)}
          />
        ))}
      </div>

      {showModal && (
        <SourceModal
          source={editingSource}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function SourceCard({ source, onEdit, onDelete }) {
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: source.color }}
            />
            <h3 className="font-semibold text-lg">{source.name}</h3>
          </div>
          <p className="text-sm text-gray-600 mt-1">{source.email}</p>
        </div>

        <div className="flex items-center space-x-2">
          <button onClick={onEdit} className="p-1 hover:bg-gray-100 rounded">
            <Edit size={16} className="text-gray-600" />
          </button>
          <button
            onClick={onDelete}
            disabled={source.id === 'unknown'}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            <Trash2 size={16} className="text-red-600" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Priorita</span>
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i < source.priority ? 'bg-primary-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Status</span>
          <span className={`badge ${source.enabled ? 'badge-success' : 'badge-error'}`}>
            {source.enabled ? 'Aktivní' : 'Neaktivní'}
          </span>
        </div>

        <div className="flex flex-wrap gap-1 mt-3">
          {source.tags.map(tag => (
            <span key={tag} className="badge badge-info">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {source.description && (
        <p className="text-sm text-gray-600 mt-4 border-t pt-4">
          {source.description}
        </p>
      )}
    </div>
  );
}

function SourceModal({ source, onSave, onClose }) {
  const [formData, setFormData] = useState({
    id: source?.id || '',
    name: source?.name || '',
    email: source?.email || '',
    enabled: source?.enabled ?? true,
    priority: source?.priority || 3,
    tags: source?.tags || [],
    color: source?.color || '#FF6B6B',
    description: source?.description || ''
  });

  const [tagInput, setTagInput] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">
          {source ? 'Upravit zdroj' : 'Přidat zdroj'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID zdroje
              </label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleChange}
                disabled={!!source}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                placeholder="tldr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Název
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="TLDR Newsletter"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="dan@tldrnewsletter.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorita (1-5)
              </label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                min="1"
                max="5"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Barva
              </label>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tagy
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag(e)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Přidat tag"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Přidat
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="badge badge-info flex items-center space-x-1"
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-xs hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Popis
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Popis zdroje"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              name="enabled"
              checked={formData.enabled}
              onChange={handleChange}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
              Aktivní zdroj
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Zrušit
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {source ? 'Uložit změny' : 'Vytvořit zdroj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
